import { useEffect, useRef, useState } from "react";
import type { ReactElement } from "react";
import type { ApiClient, ExterneReferenz, MonitorKarte, MonitorZustand }
  from "@kundeninfo/api-client";
import { monitorSprungUrl, schlechtesterZustand, ZUSTAND_RANG, ZUSTAND_LABEL,
         ZUSTAND_KLASSE } from "../monitor.js";
import { formatZeitpunkt } from "../format.js";
import { Chip, Hinweis, Karte } from "@kundeninfo/ui/me-design";
import "./KundeMonitorKarten.css";

/**
 * DIE MONITOR-KARTEN in der Übersicht eines Kunden (Spec „Monitor-Mandantensicht im
 * Kundeninfo", 08.09.2026, Stufe B) — je zugeordnetem Portal-Mandanten eine.
 *
 * Sie beantworten die Frage, für die man sonst den Monitor aufschlagen müsste: **Klemmt bei
 * diesem Kunden gerade etwas im Portal?** Zustandsmarke, drei Summen, die offenen Befunde,
 * der Stand — und der Verweis in den Monitor für alles Weitere. Details bleiben drüben; hier
 * steht nur, was auf einen Blick zu sehen sein soll.
 *
 * **NACHGERECHNET WIRD NICHTS.** `state`, `value`, `reason` und `capturedAt` kommen so, wie
 * der Monitor sie geliefert hat (Spec: „Beide Seiten zeigen dieselben Zahlen"). Formatiert
 * wird allein das Datum. Wo die Karte etwas ANORDNET — die Reihenfolge der Karten und die
 * Gesamtmarke —, tut sie das nach der Rangfolge ERROR > UNKNOWN > WARN > OK
 * (packages/web/src/monitor.ts): Hausregel „Schlechtestes oben", und unbekannt ist nie grün.
 *
 * **Jede Karte lädt für sich.** Das ist die Zusage der Spec, dass die Kundenseite nie
 * blockiert: Der Abruf hängt an keinem anderen Bestandteil der Akte, und ein Fehlschlag
 * bleibt LOKAL in seiner Karte. Zwei Mandanten, von denen einer klemmt, ergeben eine gute
 * und eine schlechte Karte — nicht zwei kaputte.
 *
 * **KEIN ZUSTAND ÜBERLEBT DEN KONTEXTWECHSEL** (Hausregel, hier teuer gelernt): Die Karten
 * hängen an Kunde UND Mandant. Beim Kundenwechsel fällt alles weg, und jede noch fliegende
 * Antwort wird verworfen (`lauf`-Nummer, Muster KundePortalMandanten.tsx) — sonst stünde
 * die Lage des vorigen Kunden über dem neuen. Der `key` an der Karte trägt beide Kennungen,
 * damit React eine Karte nicht wiederverwendet, nur weil sie an derselben Stelle steht.
 *
 * **Reine LESE-Anzeige.** Es gibt hier nichts zu bearbeiten und deshalb kein Modalpanel
 * (Hausregel „striktes Modalpanel" betrifft das Bearbeiten). Zugeordnet und entfernt wird
 * an den Portal-Mandanten in den Stammdaten.
 *
 * **Eigener CSS-Block `kmk`** — kein geteilter Blockname über Ansichten hinweg (Lektion aus
 * `.ka__`). Insbesondere NICHT `kpm`, obwohl beide dieselben Mandanten betreffen: Der eine
 * Block pflegt, der andere zeigt.
 */

/** Nur die zwei Klientenwege, die dieser Abschnitt braucht — so lässt er sich im Test mit
 *  zwei vi.fn zeichnen (Muster PortalMandantenClient). */
export type MonitorKartenClient = Pick<ApiClient,
  "listExternalReferences" | "monitorKarte">;

export type KundeMonitorKartenProps = {
  client: MonitorKartenClient;
  /** Der Kunde, dessen Mandanten hier stehen. Ein Wechsel setzt ALLES zurück. */
  customerId: string;
};

/** Eine Zeile im Zustand dieser Ansicht: die Zuordnung und, sobald sie da ist, ihre Karte. */
type Eintrag = {
  referenz: ExterneReferenz;
  /** `null` heisst „lädt noch" — und ist von „geladen, aber nichts zu zeigen" zu
   *  unterscheiden: Eine leere Karte ist eine Aussage, ein leerer Platz nicht. */
  karte: MonitorKarte | null;
  /** Der Abruf ist gescheitert, BEVOR der Server antworten konnte (Netz, 404 auf die
   *  Zuordnung). Bleibt lokal in dieser Karte. */
  fehler: string | null;
};

/**
 * Der Zustand, nach dem eine Karte einsortiert wird.
 *
 * Eine Karte OHNE Daten — noch am Laden, nicht angebunden, nicht im Monitor, kaputt — zählt
 * als `UNKNOWN` und steht damit über allem Grünen. Das ist der Punkt der Hausregel: Was
 * niemand geprüft hat, darf nicht unter den geprüften guten Fällen verschwinden.
 */
function zustandVon(eintrag: Eintrag): MonitorZustand {
  return eintrag.karte?.daten?.state ?? "UNKNOWN";
}

/** Die Erklärung einer Lage ohne Daten. Sie sagt, WAS ZU TUN ist (oder dass nichts zu tun
 *  ist) — eine Karte, die nur „Fehler" sagt, hat dem Leser nichts gegeben. */
const LAGE_TEXT: Record<Exclude<MonitorKarte["lage"], "ANGEZEIGT">, string> = {
  NICHT_ANGEBUNDEN:
    "Monitor nicht angebunden. Für dieses System ist kein Zugang zum Monitor "
    + "eingerichtet; die Zuordnung der Portal-Mandanten bleibt davon unberührt.",
  NICHT_IM_MONITOR:
    "Im Monitor nicht angezeigt. Der Monitor beobachtet diesen Mandanten nicht — dort "
    + "läuft für ihn kein Vertrag mehr, oder er ist ausgeblendet.",
  KONFIGURATIONSFEHLER:
    "Der Monitor hat den Zugang abgewiesen. Das ist ein Konfigurationsfehler und nichts, "
    + "was sich hier beheben lässt — bitte an die Betreuung dieses Systems geben.",
  NICHT_ERREICHBAR:
    "Monitor nicht erreichbar. Die Zahlen fehlen deshalb gerade; alles andere an diesem "
    + "Kunden ist davon unberührt.",
};

/** Monitor-Zustand → Status-Art des Chips (me-chip `status-art`, P12 W2). `UNKNOWN` ist
 *  grau, nie grün (Hausregel). */
const STATUS_ART: Record<MonitorZustand, "ok" | "warnung" | "fehler" | "unbekannt"> = {
  OK: "ok", WARN: "warnung", ERROR: "fehler", UNKNOWN: "unbekannt",
};

function Marke({ zustand }: { zustand: MonitorZustand }): ReactElement {
  return (
    <Chip className="kmk__marke" statusArt={STATUS_ART[zustand]}>
      {ZUSTAND_LABEL[zustand]}
    </Chip>
  );
}

/**
 * Der Verweis „Im Monitor ansehen" (Stufe A).
 *
 * Neues Fenster mit `rel="noopener noreferrer"`: `noopener` nimmt der Zielseite das
 * `window.opener`, mit dem sie diese Anwendung umnavigieren könnte; `noreferrer` hält
 * zusätzlich den Referrer zurück — die Adresse dieser Seite trägt die Kundenkennung, und
 * die hat in einem Kopf an einen anderen Dienst nichts verloren, auch nicht an den
 * Monitor. Ohne eingerichtete Basis-Adresse gibt es KEINEN Verweis — dann steht hier
 * nichts, und nicht etwa ein toter Knopf.
 */
function MonitorVerweis(
  { basisUrl, externeId }: { basisUrl: string | null; externeId: string },
): ReactElement | null {
  const ziel = monitorSprungUrl(basisUrl, externeId);
  if (ziel === null) return null;
  return (
    <a className="kmk__verweis" href={ziel} target="_blank" rel="noopener noreferrer"
      aria-label={`Mandant ${externeId} im Monitor ansehen`}>
      Im Monitor ansehen
    </a>
  );
}

export function KundeMonitorKarten(p: KundeMonitorKartenProps): ReactElement | null {
  /** `null` heisst „noch nicht geladen" — von „geladen und leer" zu unterscheiden. */
  const [eintraege, setEintraege] = useState<Eintrag[] | null>(null);
  const [basisUrl, setBasisUrl] = useState<string | null>(null);
  const [ladeFehler, setLadeFehler] = useState<string | null>(null);

  /**
   * Die laufende NUMMER dieser Akte — der Riegel gegen Antworten, die zu spät kommen
   * (Muster KundePortalMandanten.tsx, dort teuer gelernt).
   *
   * Hier wiegt er schwerer als dort, weil die Karten NACHEINANDER eintreffen: Ein Abruf über
   * ein langsames Netz kann noch unterwegs sein, wenn längst ein anderer Kunde auf dem
   * Bildschirm steht. Ohne diese Nummer schriebe er seine Zahlen in dessen Karte — und die
   * sähe völlig plausibel aus.
   */
  const lauf = useRef(0);

  const { client, customerId } = p;
  const listExternalReferences = client.listExternalReferences;
  const monitorKarte = client.monitorKarte;

  useEffect(() => {
    const meiner = ++lauf.current;
    const nochAktuell = (): boolean => meiner === lauf.current;
    setEintraege(null);
    setBasisUrl(null);
    setLadeFehler(null);

    listExternalReferences(customerId)
      .then((antwort) => {
        if (!nochAktuell()) return;
        const items = antwort.items;
        const basis = antwort.monitorBasisUrl ?? null;
        setBasisUrl(basis);
        setEintraege(items.map((referenz) => ({ referenz, karte: null, fehler: null })));
        /*
         * OHNE eingerichteten Monitor wird NICHTS gerufen. Die Antwort stünde ohnehin fest
         * (`NICHT_ANGEBUNDEN`, dieselbe Entscheidung serverseitig), und ein Abruf je
         * Mandant, dessen Ergebnis man kennt, ist kein Abruf, sondern Lärm. Die Übersicht
         * sagt es stattdessen EINMAL — siehe unten.
         */
        if (basis === null) return;
        /*
         * Und nun je Mandant ein EIGENER Abruf — nebeneinander, nicht nacheinander: Die
         * Karten sind voneinander unabhängig, und ein langsamer Mandant darf die übrigen
         * nicht aufhalten. Jede Antwort schreibt NUR ihre eigene Zeile (Suche über die
         * Referenz-Kennung, nicht über den Listenplatz — die Liste kann sich zwischenzeitlich
         * geändert haben).
         */
        for (const referenz of items) {
          monitorKarte(customerId, referenz.id)
            .then((karte) => {
              if (!nochAktuell()) return;
              setEintraege((bisher) => bisher === null ? bisher : bisher.map(
                (e) => e.referenz.id === referenz.id ? { ...e, karte } : e));
            })
            .catch(() => {
              if (!nochAktuell()) return;
              // Der Fehler bleibt LOKAL in dieser Karte (Spec) — die übrigen Karten und
              // der Rest der Akte merken nichts davon.
              setEintraege((bisher) => bisher === null ? bisher : bisher.map(
                (e) => e.referenz.id === referenz.id
                  ? { ...e, fehler: "Die Monitor-Karte konnte nicht geladen werden." }
                  : e));
            });
        }
      })
      .catch(() => {
        if (!nochAktuell()) return;
        setEintraege([]);
        setLadeFehler("Die Portal-Mandanten konnten nicht geladen werden.");
      });

    // Auch das ABRÄUMEN zählt hoch: Beim Ausbauen sind alle fliegenden Antworten ungültig.
    return () => { lauf.current += 1; };
  }, [listExternalReferences, monitorKarte, customerId]);

  /*
   * Solange nichts geladen ist, steht hier NICHTS — kein Platzhalter, kein „Lädt …".
   * Die Übersicht ist der erste Blick auf einen Kunden; ein Kasten, der erst nach einer
   * Antwort weiss, ob er überhaupt etwas zu sagen hat, würde bei jedem Kundenwechsel
   * aufblitzen und den Rest der Seite verschieben.
   */
  if (eintraege === null) return null;
  if (ladeFehler !== null) {
    return (
      <section className="kmk" aria-label="Monitor">
        <Hinweis art="fehler" className="kmk__meldung">{ladeFehler}</Hinweis>
      </section>
    );
  }
  /*
   * KEIN Mandant zugeordnet → gar kein Abschnitt. „Monitor nicht angebunden" wäre hier eine
   * Auskunft über etwas, wonach niemand gefragt hat; dass dieser Kunde für den Monitor
   * unsichtbar ist, sagt der Abschnitt „Portal-Mandanten" in den Stammdaten — dort, wo man
   * es auch ändern kann.
   */
  if (eintraege.length === 0) return null;
  /*
   * NICHT ANGEBUNDEN: keine Karte, EIN Satz (Spec). N Karten, die alle dasselbe sagen,
   * wären N-mal dieselbe Auskunft — und sie sähen aus wie N Störungen. Der Verweis „Im
   * Monitor ansehen" fällt aus demselben Grund weg: Es gibt keine Adresse, und eine
   * erfundene wäre schlimmer als keine.
   */
  if (basisUrl === null) {
    return (
      <section className="kmk" aria-label="Monitor">
        <div className="kmk__kopf"><h3 className="kmk__titel">Monitor</h3></div>
        <p className="kmk__hinweis">
          Monitor nicht angebunden. Für dieses System ist kein Zugang zum Monitor
          eingerichtet; die Zuordnung der Portal-Mandanten bleibt davon unberührt.
        </p>
      </section>
    );
  }

  // Schlechtestes oben. `toSorted` liesse den Zustand unangetastet, ist aber jung — eine
  // Kopie tut dasselbe und ist überall zu Hause.
  const sortiert = [...eintraege].sort(
    (a, b) => ZUSTAND_RANG[zustandVon(a)] - ZUSTAND_RANG[zustandVon(b)]);
  const gesamt = schlechtesterZustand(eintraege.map(zustandVon));

  return (
    <section className="kmk" aria-label="Monitor">
      <div className="kmk__kopf">
        <h3 className="kmk__titel">Monitor</h3>
        {/* Die GESAMTMARKE: der schlechteste Zustand über alle Mandanten dieses Kunden.
            Sie steht im Kopf, damit sie auch dann zu sehen ist, wenn die Karten
            weiterunten stehen — und sie folgt derselben Rangfolge wie die Sortierung. */}
        <Marke zustand={gesamt} />
      </div>

      <ul className="kmk__liste">
        {sortiert.map((eintrag) => (
          // Der Schlüssel trägt KUNDE und MANDANT: Eine Karte darf nicht deshalb
          // wiederverwendet werden, weil sie beim nächsten Kunden an derselben Stelle steht.
          <li key={`${customerId}:${eintrag.referenz.id}`} className="kmk__eintrag">
            <Karte className="kmk__karte" titel={(
              <div className="kmk__kartenkopf">
                <Chip className="kmk__system">{eintrag.referenz.systemKey}</Chip>
                <span className="kmk__schluessel">{eintrag.referenz.externalId}</span>
                {eintrag.karte !== null && eintrag.karte.daten !== null && (
                  <Marke zustand={eintrag.karte.daten.state} />
                )}
                <MonitorVerweis basisUrl={basisUrl}
                  externeId={eintrag.referenz.externalId} />
              </div>
            )}>

              {eintrag.fehler !== null && (
                <Hinweis art="fehler" className="kmk__meldung">{eintrag.fehler}</Hinweis>
              )}

              {eintrag.fehler === null && eintrag.karte === null && (
                <p className="kmk__hinweis" role="status">Lädt …</p>
              )}

              {eintrag.karte !== null && eintrag.karte.lage !== "ANGEZEIGT" && (
                <p className="kmk__hinweis">{LAGE_TEXT[eintrag.karte.lage]}</p>
              )}

              {eintrag.karte !== null && eintrag.karte.daten !== null && (
                <>
                  {eintrag.karte.daten.reason !== null && (
                    <p className="kmk__grund">{eintrag.karte.daten.reason}</p>
                  )}

                  {/* Die Summen — Titel, Zahl und Zustand so, wie der Monitor sie nennt.
                      Eine fehlende Zahl steht als „—" und nicht als 0: „keine Angabe" und
                      „nichts offen" sind zweierlei. */}
                  <ul className="kmk__summen">
                    {eintrag.karte.daten.bestaende.map((b) => (
                      <li key={b.key} className="kmk__summe">
                        <span className="kmk__summe-titel">{b.titel}</span>
                        <span
                          className={`kmk__summe-wert kmk__summe-wert--${ZUSTAND_KLASSE[b.state]}`}
                        >
                          {b.value === null ? "—" : b.value.toLocaleString("de-DE")}
                        </span>
                      </li>
                    ))}
                  </ul>

                  {eintrag.karte.daten.befunde.length > 0 && (
                    <ul className="kmk__befunde">
                      {eintrag.karte.daten.befunde.map((f) => (
                        <li key={f.id} className="kmk__befund">
                          <Marke zustand={f.severity} />
                          <span className="kmk__befund-grund">{f.reason}</span>
                          {f.openedAt !== null && (
                            <span className="kmk__befund-seit">
                              seit {formatZeitpunkt(f.openedAt)}
                            </span>
                          )}
                        </li>
                      ))}
                    </ul>
                  )}

                  {eintrag.karte.daten.capturedAt !== null && (
                    <p className="kmk__stand">
                      Stand: {formatZeitpunkt(eintrag.karte.daten.capturedAt)}
                    </p>
                  )}
                </>
              )}
            </Karte>
          </li>
        ))}
      </ul>
    </section>
  );
}
